<template>
  <b-card
    :title="`Verify ${snakeToTitleCase($route.params.field)}`"
    body-class="mx-3 mx-md-5 mx-lg-5 mx-xl-5 px-3 px-md-5 px-lg-5 px-xl-5"
    class="mt-5 p-0 p-md-5 p-lg-5 p-xl-5 w-75"
    style="/**width: 550px**/"
  >
    <b-form onkeydown="return event.key != 'Enter'">
      <p class="pb-2 grey--text">
        <template v-if="$route.params.field === 'email'">
          Enter the One-Time Code from the original email we sent you.
        </template>
        <template v-else>
          Enter the One-Time Code sent to your
          {{ snakeToTitleCase($route.params.field) }}
        </template>
      </p>

      <b-form-group id="input-group-code">
        <code-input
          :key="codeInputKey"
          :loading="false"
          @complete="onCompleteCode"
        />

        <div class="invalid-feedback d-block mt-2">{{ errors.code }}</div>

        <div class="mt-2">
          <b-button
            variant="link"
            class="p-0 resend-code-btn"
            :disabled="resending || loading || cooldown > 0"
            @click="onResendCode"
          >
            Resend code
            <span v-if="cooldown > 0">({{ cooldown }}s)</span>
          </b-button>
          <span v-if="resendNotice" class="text-success ml-2 small">{{
            resendNotice
          }}</span>
        </div>
      </b-form-group>

      <div class="justify-content-between">
        <b-container fluid>
          <b-row cols="2" class="verify-actions">
            <b-col cols="6" class="text-center text-lg-right text-xl-right">
              <b-button
                class="w-75 mr-0 mr-lg-2 mr-xl-2 btn-outlined bg-white text-dark"
                variant="primary"
                :to="{ name: 'user.settings-page', hash: '#account' }"
              >
                Cancel
              </b-button>
            </b-col>
            <b-col cols="6" class="text-center text-lg-left text-xl-left">
              <b-button
                variant="primary"
                class="w-75 ml-0 ml-lg-2 ml-xl-2"
                :disabled="loading"
                @click="onCompleteCode"
              >
                <b-spinner v-if="loading" small label="Loading" />&nbsp;Continue
              </b-button>
            </b-col>
          </b-row>
        </b-container>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CodeInput from '@/components/inputs/CodeInput'

export default {
  components: {
    CodeInput,
  },

  computed: {
    ...mapState({
      verificationToken: (state) => state.auth.verificationToken,
      user: (state) => state.auth.user,
    }),
  },

  data() {
    return {
      loading: false,
      resending: false,
      cooldown: 0,
      cooldownTimer: null,
      resendNotice: null,
      codeInputKey: 0,
      form: {
        code: null,
        field: this.$route.params.field,
      },
      errors: {
        code: null,
      },
    }
  },

  created() {
    this.loading = true
    this.resendVerificationCode(this.form.field)
      .then(() => this.startCooldown())
      .catch((err) => console.log(err))
      .finally(() => (this.loading = false))
  },

  beforeDestroy() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer)
      this.cooldownTimer = null
    }
  },

  methods: {
    startCooldown(seconds = 30) {
      this.cooldown = seconds
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
      }
      this.cooldownTimer = setInterval(() => {
        if (this.cooldown <= 1) {
          clearInterval(this.cooldownTimer)
          this.cooldownTimer = null
          this.cooldown = 0
        } else {
          this.cooldown -= 1
        }
      }, 1000)
    },

    onResendCode() {
      if (this.resending || this.loading || this.cooldown > 0) return

      this.resending = true
      this.resendNotice = null
      this.errors.code = null

      this.resendVerificationCode(this.form.field)
        .then(() => {
          this.resendNotice = 'A new code has been sent.'
          this.codeInputKey += 1
          this.startCooldown()
        })
        .catch((error) => {
          const d = error.response && error.response.data
          const msg =
            (d && d.message) ||
            'Unable to resend code. Please try again shortly.'
          this.$bvToast.toast(msg, {
            title: 'Resend failed',
            variant: 'danger',
            autoHideDelay: 5000,
            solid: true,
          })
        })
        .finally(() => (this.resending = false))
    },

    ...mapActions({
      resendVerificationCode: 'auth/resendVerificationCode',
      submitVerificationCode: 'auth/submitVerificationCode',
    }),

    snakeToTitleCase(str) {
      return str
        .replace(/_/g, ' ')
        .replace(
          /\w\S*/g,
          (word) =>
            word.charAt(0).toUpperCase() + word.substring(1).toLowerCase(),
        )
    },

    onCompleteCode(code) {
      this.loading = true
      this.form.code = code

      this.submitVerificationCode({
        token: this.form.code,
        via: this.form.field,
      })
        .then(() => {
          this.$router.push({
            name: 'user.settings-page',
            hash: '#account',
          })
        })
        .catch((error) => {
          const d = error.response && error.response.data
          const msg = d
            ? d.message ||
              (d.errors && Object.values(d.errors).flat()[0]) ||
              'Invalid or expired verification code.'
            : error.message || 'Invalid or expired verification code.'
          if (d && d.errors) {
            const flat = Object.values(d.errors).flat()
            this.errors.code = flat[0] || d.message || msg
          } else {
            this.errors.code = msg
          }
          this.$bvToast.toast(msg, {
            title: 'Verification failed',
            variant: 'danger',
            autoHideDelay: 5000,
            solid: true,
          })
        })
        .finally(() => (this.loading = false))
    },
  },
}
</script>

<style lang="scss" scoped>
.btn.btn-outlined {
  padding: 4px 32px;
}
.btn {
  height: 36px;
}

/* Large screens: the buttons sit as a natural-width pair on the left, aligned
   with the code boxes and the rest of the content (the container padding and
   the row's negative margin cancel out at the left edge). */
@media (min-width: 992px) {
  .verify-actions {
    justify-content: flex-start;
  }

  /* Each column matches the old button footprint (75% of a half-width column
     = 37.5% of the row) so the buttons keep their original size, just packed
     together on the left. */
  .verify-actions > [class*='col'] {
    flex: 0 0 auto;
    width: 37.5%;
    max-width: none;
  }

  .verify-actions .btn {
    width: 100% !important;
  }
}

/* Small screens (< lg): centre the heading + description with the code boxes,
   let the card use the full width, trim its heavy body padding, and make each
   button fill its half-column so the side-by-side pair fits down to 320px. */
@media (max-width: 991.98px) {
  .card.w-75 {
    width: 100% !important;
    margin-top: 2rem !important;
  }

  .card /deep/ .card-body {
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding: 1.25rem !important;
  }

  .card /deep/ .card-title,
  .card .grey--text {
    text-align: center;
  }

  .verify-actions .btn {
    width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  /* Clear gap between the side-by-side Cancel / Continue buttons. */
  .verify-actions > [class*='col']:first-child {
    padding-right: 0.5rem !important;
  }

  .verify-actions > [class*='col']:last-child {
    padding-left: 0.5rem !important;
  }
}
</style>
